import { styled } from "@mui/material/styles";
import {
  OutlinedInput,
  InputLabel,
  Typography,
  InputAdornment,
} from "@mui/material";

type InputStyleProps = {
  width?: string;
  height?: string;
  minHeight?: string;
  borderRadius?: string;
  fontSize?: "sm";
};

const StyledInputContainer = styled("div")`
  max-width: 100%;
  position: relative;
`;

const StyledInputLabel = styled(InputLabel)`
  width: max-content;
  font-size: ${({ theme }) => theme.typography.bodyM?.fontSize};
  line-height: ${({ theme }) => theme.typography.bodyM?.lineHeight};
  color: ${({ theme }) => theme.palette.common.black};
  margin-bottom: 5px;
  font-weight: 300;
  user-select: none;

  & > .MuiInputLabel-asterisk {
    color: ${({ theme }) => theme.palette.primary.main};
  }

  &.Mui-disabled {
    color: ${({ theme }) => theme.palette.customGrey.main};
  }
`;

const StyledInput = styled(OutlinedInput)<InputStyleProps>`
  width: ${({ width }) => width ?? "100%"};
  min-height: ${({ minHeight, height }) => !height && (minHeight ?? "48px")};
  height: ${({ height }) => height};
  background-color: ${({ theme }) => theme.palette.customGrey.soft};
  font-size: ${({ theme, fontSize }) =>
    fontSize === "sm"
      ? theme.typography.bodyM?.fontSize
      : theme.typography.bodyL?.fontSize};
  line-height: ${({ theme, fontSize }) =>
    fontSize === "sm"
      ? theme.typography.bodyM?.lineHeight
      : theme.typography.bodyL?.lineHeight};
  color: ${({ theme }) => theme.palette.common.black};
  border-radius: ${({ borderRadius }) => borderRadius ?? "4px"};
  box-sizing: border-box;

  &:hover {
    @media (min-width: 1024px) {
      & .MuiOutlinedInput-notchedOutline {
        border-color: ${({ theme }) => theme.palette.primary.main};
        transition: all 0.2s linear;
        border-width: 1px;
      }

      & .input-password-btn > svg {
        stroke: ${({ theme }) => theme.palette.primary.main};
      }
    }
  }

  &.Mui-focused {
    & .MuiOutlinedInput-notchedOutline {
      border-color: ${({ theme }) => theme.palette.primary.main};
      transition: all 0.2s linear;
      border-width: 1px;
    }

    & .input-password-btn > svg {
      stroke: ${({ theme }) => theme.palette.primary.main};
    }
  }

  &.MuiInputBase-multiline {
    padding: 0;
  }

  & > .MuiInputBase-input {
    box-sizing: border-box;
    height: 100%;
    padding: 12px 24px;
    width: 100%;
    caret-color: ${({ theme }) => theme.palette.primary.main};

    &[type="number"]::-webkit-inner-spin-button,
    &[type="number"]::-webkit-outer-spin-button {
      -webkit-appearance: none;
      margin: 0;
      display: none;
    }

    &[type="number"] {
      -moz-appearance: textfield;
    }
    &[type="number"]:hover,
    &[type="number"]:focus {
      -moz-appearance: number-input;
    }

    &::placeholder {
      color: ${({ theme }) => theme.palette.customGrey.main};
      font-size: ${({ theme, fontSize }) =>
        fontSize === "sm"
          ? theme.typography.bodyM?.fontSize
          : theme.typography.bodyL?.fontSize};
      line-height: ${({ theme, fontSize }) =>
        fontSize === "sm"
          ? theme.typography.bodyM?.lineHeight
          : theme.typography.bodyL?.lineHeight};
      opacity: 1;
      font-weight: 300;
    }

    &.Mui-disabled {
      color: ${({ theme }) => theme.palette.customGrey.main};
      -webkit-text-fill-color: ${({ theme }) => theme.palette.customGrey.main};
    }
  }

  & > .MuiOutlinedInput-notchedOutline {
    border-width: 1px;
    border-color: transparent;
    transition: all 0.2s linear;
  }

  &.Mui-error {
    background-color: ${({ theme }) => theme.palette.red.light};

    & > .MuiOutlinedInput-notchedOutline {
      border-color: ${({ theme }) => theme.palette.red.main};
    }
  }

  &.input_success {
    background-color: ${({ theme }) => theme.palette.green.light};

    & > .MuiOutlinedInput-notchedOutline {
      border-color: ${({ theme }) => theme.palette.green.main};
    }
  }

  &.input_white {
    background-color: ${({ theme }) => theme.palette.common.white};
  }

  &.Mui-disabled > .MuiOutlinedInput-notchedOutline {
    border-color: transparent;
  }

  &.MuiInputBase-adornedEnd {
    padding-right: 24px;

    & > .MuiInputBase-input {
      padding-right: 5px;
    }
  }

  &.MuiInputBase-adornedStart {
    padding-left: 24px;

    & > .MuiInputBase-input {
      padding-left: 5px;
    }
  }
`;

const StyledInputError = styled(Typography)`
  display: inline-block;
  opacity: 0;
  visibility: hidden;
  color: ${({ theme }) => theme.palette.red.main};
  transition: all 0.2s linear;
  margin-top: 5px;

  &.input-error_visible {
    opacity: 1;
    visibility: visible;
    transition: all 0.2s linear;
  }
`;

const StyledInputAdornment = styled(InputAdornment)`
  & > svg {
    height: 18px;
    width: 18px;
  }

  & > .input-password-btn {
    & > svg {
      height: 18px;
      width: 18px;
      transition: all 0.2s linear;
    }

    &:hover {
      @media (min-width: 1024px) {
        & > svg {
          stroke: ${({ theme }) => theme.palette.primary.main};
          transition: all 0.2s linear;
        }
      }
    }
  }
`;

export {
  StyledInputContainer,
  StyledInputLabel,
  StyledInput,
  StyledInputError,
  StyledInputAdornment,
};
